<script setup>
import {
  computed,
  ref,
  onMounted,
  nextTick,
  getCurrentInstance,
  useAttrs,
  watch,
} from 'vue';
import FontIcon from 'dashboard/components-next/icon/FontIcon.vue';
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  customInputClass: { type: [String, Object, Array], default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  id: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  variant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'plain'].includes(value),
  },
  message: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'input',
  'focus',
  'enter',
]);

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const inputAttrs = computed(() => {
  const attributes = { ...attrs };
  delete attributes.class;
  return attributes;
});

// Generate a unique ID per component instance when `id` prop is not provided.
const { uid } = getCurrentInstance();
const uniqueId = computed(() => props.id || `input-${uid}`);

const isFocused = ref(false);
const inputRef = ref(null);
const currentValue = ref(props.modelValue);
const hasSearchValue = computed(
  () =>
    props.type === 'search' &&
    !props.disabled &&
    String(currentValue.value).length > 0
);

watch(
  () => props.modelValue,
  value => {
    currentValue.value = value;
  }
);

const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const inputOutlineClass = computed(() => {
  if (props.type === 'search') return '';

  switch (props.messageType) {
    case 'error':
      return 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8';
    default:
      return 'outline-[rgba(53,60,69,0.16)] dark:outline-n-weak hover:outline-n-brand dark:hover:outline-n-brand disabled:outline-n-weak dark:disabled:outline-n-weak focus:outline-n-brand dark:focus:outline-n-brand';
  }
});

const inputSurfaceClass = computed(() => {
  if (props.type === 'search') {
    return '!border-0 !outline-none !bg-transparent';
  }

  return 'outline outline-1 outline-offset-[-1px] rounded-lg bg-transparent';
});

const handleInput = event => {
  let value = event.target.value;
  // Convert to number if type is number and value is not empty
  if (props.type === 'number' && value !== '') {
    value = Number(value);
  }
  currentValue.value = value;
  emit('update:modelValue', value);
  emit('input', event);
};

const handleFocus = event => {
  emit('focus', event);
  isFocused.value = true;
};

const sizeClass = computed(() => {
  if (props.type === 'search') {
    return 'h-[30px] !px-0 !py-0 leading-[30px]';
  }

  switch (props.size) {
    case 'sm':
      return 'h-[30px] !px-2.5 !py-2';
    case 'md':
      return 'h-[30px] !px-2.5 !py-2';
    default:
      return 'h-[30px] !px-2.5 !py-2';
  }
});

const inputTypographyClass = computed(() => [
  'font-arabic',
  props.type === 'search' ? 'font-normal' : '',
]);

const fieldClass = computed(() => {
  const heightClass = props.variant === 'default' ? 'min-h-8' : 'min-h-0';

  if (props.type !== 'search') return heightClass;

  const borderClass =
    props.messageType === 'error'
      ? 'border-n-ruby-8 hover:border-n-ruby-9 focus-within:!border-n-ruby-9 dark:border-n-ruby-8 dark:hover:border-n-ruby-9 dark:focus-within:!border-n-ruby-9'
      : 'border-n-slate-6 hover:border-n-brand/50 focus-within:!border-n-brand dark:border-n-weak dark:hover:border-n-brand/60 dark:focus-within:!border-n-brand';

  return [
    heightClass,
    borderClass,
    'rounded-lg border border-solid bg-n-slate-3 transition-colors duration-150 dark:bg-[#2F2F32]',
  ];
});

const handleBlur = event => {
  emit('blur', event);
  isFocused.value = false;
};

const handleEnter = event => {
  emit('enter', event);
};

const clearSearch = () => {
  inputRef.value.value = '';
  inputRef.value.dispatchEvent(new Event('input', { bubbles: true }));
  inputRef.value.focus();
};

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
});
</script>

<template>
  <div class="group relative flex min-w-0 flex-col gap-1" :class="attrs.class">
    <label
      v-if="label || $slots['label-suffix']"
      :for="uniqueId"
      class="flex items-center gap-1 text-xs font-semibold text-n-slate-11"
    >
      {{ label }}
      <slot name="label-suffix" />
    </label>
    <slot v-if="type !== 'search'" name="prefix" />
    <div
      class="relative flex w-full min-w-0 items-center"
      :class="fieldClass"
      data-input-field
    >
      <div
        v-if="type === 'search'"
        class="flex h-[30px] w-10 shrink-0 items-center justify-center"
        data-input-icon
      >
        <FontIcon
          icon="search-outline"
          :stroke-width="1.5"
          class="pointer-events-none shrink-0 text-[22px] text-n-slate-12 dark:text-n-slate-11"
        />
      </div>
      <div
        class="flex min-w-0 items-center"
        :class="
          type === 'search' ? 'h-[30px] flex-1 ltr:pr-2 rtl:pl-2' : 'w-full'
        "
        data-input-control
      >
        <input
          :id="uniqueId"
          v-bind="inputAttrs"
          ref="inputRef"
          :value="currentValue"
          :dir="inputAttrs.dir || 'auto'"
          :class="[
            customInputClass,
            inputOutlineClass,
            inputSurfaceClass,
            sizeClass,
            inputTypographyClass,
            {
              error: messageType === 'error',
              focus: isFocused,
              'min-w-0 flex-1': type === 'search',
              'w-full': type !== 'search',
            },
          ]"
          :type="type"
          :placeholder="placeholder"
          :disabled="disabled"
          :aria-invalid="
            inputAttrs['aria-invalid'] ?? (messageType === 'error' || undefined)
          "
          :aria-describedby="
            [
              inputAttrs['aria-describedby'],
              message ? `${uniqueId}-message` : null,
            ]
              .filter(Boolean)
              .join(' ') || undefined
          "
          :min="
            ['date', 'datetime-local', 'time', 'number'].includes(type)
              ? min
              : undefined
          "
          :max="
            ['date', 'datetime-local', 'time', 'number'].includes(type)
              ? max
              : undefined
          "
          class="n-input-element block reset-base text-start text-sm !mb-0 border-none border-0 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-[#8A8F9D] disabled:cursor-not-allowed disabled:bg-transparent disabled:opacity-50 text-n-slate-12 dark:text-white transition-all duration-500 ease-in-out [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-search-cancel-button]:appearance-none"
          data-input-element
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter="handleEnter"
        />
        <button
          v-if="hasSearchValue"
          type="button"
          class="invisible flex size-5 shrink-0 items-center justify-center text-n-black opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100 dark:text-n-slate-12"
          :aria-label="label || placeholder"
          data-input-clear
          @mousedown.prevent
          @click="clearSearch"
        >
          <span
            class="i-lucide-square-x inline-flex size-[18px] min-h-[18px] min-w-[18px] shrink-0 items-center justify-center text-[18px] leading-none"
          />
        </button>
      </div>
      <slot name="suffix" />
    </div>
    <p
      v-if="message"
      :id="`${uniqueId}-message`"
      class="mb-0 min-w-0 truncate text-xs font-normal transition-all duration-500 ease-in-out"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>
